import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Plus, Search, X, Kanban, List, Calendar, Download, Settings2, ChevronRight, Archive } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import AppShell from '../components/AppShell';
import CRMBoard from '../components/crm/CRMBoard';
import CRMList from '../components/crm/CRMList';
import CRMCalendar from '../components/crm/CRMCalendar';
import PipelineStageManager from '../components/crm/PipelineStageManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CRM — container page owning the shared state (stages + grouped contacts)
 * and switching between Board / List / Calendar sub-views.
 *
 * Stages come from the server via GET /api/crm/pipeline/stages so users
 * can customize their pipeline. `showArchived` toggles the Closed Won /
 * Closed Lost lanes visibility across every view.
 */
export default function CRM() {
  const { user, loading: authLoading, getAuthHeader } = useAuth();
  const navigate = useNavigate();

  const [stages, setStages] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('board'); // 'board' | 'list' | 'calendar'
  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [showStageManager, setShowStageManager] = useState(false);

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', notes: '', stage: 'lead', tags: [],
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    fetchContacts();
  }, [user, authLoading]);

  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/crm/contacts/stages`, { headers: getAuthHeader() });
      setStages(res.data.stages || []);
      setGrouped(res.data.grouped || {});
      // Ensure the add-form default stage matches an existing non-archived one.
      const firstActive = (res.data.stages || []).find((s) => !s.is_archived);
      if (firstActive) {
        setFormData((f) => ({ ...f, stage: f.stage || firstActive.id }));
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/crm/contacts`, formData, { headers: getAuthHeader() });
      toast.success('Contact added successfully');
      setShowAddModal(false);
      resetForm();
      fetchContacts();
    } catch (_err) {
      toast.error('Failed to add contact');
    }
  };

  const handleUpdateContact = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/crm/contacts/${editingContact.id}`, formData, { headers: getAuthHeader() });
      toast.success('Contact updated');
      setEditingContact(null);
      resetForm();
      fetchContacts();
    } catch (_err) {
      toast.error('Failed to update contact');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await axios.delete(`${API_URL}/api/crm/contacts/${contactId}`, { headers: getAuthHeader() });
      toast.success('Contact deleted');
      fetchContacts();
    } catch (_err) {
      toast.error('Failed to delete contact');
    }
  };

  const handleStageChange = async (contactId, newStage) => {
    try {
      await axios.put(
        `${API_URL}/api/crm/contacts/${contactId}/stage?stage=${encodeURIComponent(newStage)}`,
        {},
        { headers: getAuthHeader() },
      );
      // Optimistic: also mutate grouped so the UI doesn't flicker while
      // waiting on the next fetch.
      const stageLabel = stages.find((s) => s.id === newStage)?.label || newStage;
      toast.success(`Moved to ${stageLabel}`);
      fetchContacts();
    } catch (_err) {
      toast.error('Failed to move contact');
    }
  };

  const resetForm = () => {
    const firstActive = stages.find((s) => !s.is_archived);
    setFormData({ name: '', email: '', phone: '', company: '', notes: '', stage: firstActive?.id || 'lead', tags: [] });
  };

  const openEditModal = (contact) => {
    setFormData({
      name: contact.name,
      email: contact.email || '',
      phone: contact.phone || '',
      company: contact.company || '',
      notes: contact.notes || '',
      stage: contact.stage,
      tags: contact.tags || [],
    });
    setEditingContact(contact);
  };

  if (authLoading || loading) {
    return (
      <AppShell active="crm" title="CRM">
        <div className="min-h-full flex items-center justify-center">
          <div className="w-12 h-12 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
        </div>
      </AppShell>
    );
  }

  const totalCount = Object.values(grouped).reduce((sum, list) => sum + list.length, 0);
  const wonCount = stages.filter((s) => s.is_won).reduce((sum, s) => sum + (grouped[s.id]?.length || 0), 0);
  const lostCount = stages.filter((s) => s.is_lost).reduce((sum, s) => sum + (grouped[s.id]?.length || 0), 0);

  const exportCsv = () => {
    const rows = [['Name', 'Company', 'Email', 'Phone', 'Stage', 'Updated']];
    const stageMap = Object.fromEntries(stages.map((s) => [s.id, s.label]));
    Object.entries(grouped).forEach(([stageId, list]) => {
      list.forEach((c) => rows.push([
        c.name || '', c.company || '', c.email || '', c.phone || '',
        stageMap[stageId] || stageId, c.updated_at || c.created_at || '',
      ]));
    });
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `crm_contacts_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AppShell active="crm" title="CRM">
      {/* Breadcrumb + toolbar */}
      <div className="border-b border-white/5 bg-brand-bg/80 backdrop-blur-md px-6 py-3 flex items-center justify-between flex-wrap gap-3" data-testid="crm-breadcrumb">
        <div className="flex items-center gap-3 text-sm">
          <span className="text-gray-500">CRM</span>
          <ChevronRight className="w-3.5 h-3.5 text-gray-600" />
          <span className="text-white font-medium">Pipeline</span>
          <span className="ml-2 px-2 py-0.5 rounded-full bg-brand-gold/15 text-brand-gold text-[10px]">
            {totalCount} contacts
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search contacts, deals…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 rounded-full text-sm text-white h-9"
              data-testid="search-contacts"
            />
          </div>
          <Button onClick={() => setShowAddModal(true)} className="bg-brand-blue text-white text-xs rounded-lg hover:bg-brand-blue/80" data-testid="add-contact-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Create
          </Button>
        </div>
      </div>

      {/* View toggle + counts + settings */}
      <div className="border-b border-white/5 bg-brand-bg px-6 py-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('board')}
            className={`px-3 py-1 rounded-md text-xs ${viewMode === 'board' ? 'border border-brand-gold/40 bg-brand-gold/15 text-brand-gold' : 'border border-white/10 text-gray-400 hover:bg-white/5'}`}
            data-testid="view-board"
          >
            <Kanban className="inline w-3 h-3 mr-1" /> Board
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-3 py-1 rounded-md text-xs ${viewMode === 'list' ? 'border border-brand-gold/40 bg-brand-gold/15 text-brand-gold' : 'border border-white/10 text-gray-400 hover:bg-white/5'}`}
            data-testid="view-list"
          >
            <List className="inline w-3 h-3 mr-1" /> List
          </button>
          <button
            onClick={() => setViewMode('calendar')}
            className={`px-3 py-1 rounded-md text-xs ${viewMode === 'calendar' ? 'border border-brand-gold/40 bg-brand-gold/15 text-brand-gold' : 'border border-white/10 text-gray-400 hover:bg-white/5'}`}
            data-testid="view-calendar"
          >
            <Calendar className="inline w-3 h-3 mr-1" /> Calendar
          </button>
          <button
            onClick={() => setShowArchived(!showArchived)}
            className={`ml-2 px-3 py-1 rounded-md text-xs ${showArchived ? 'border border-emerald-500/40 bg-emerald-500/15 text-emerald-300' : 'border border-white/10 text-gray-400 hover:bg-white/5'}`}
            data-testid="toggle-archived"
            title="Show/hide closed (archived) deals"
          >
            <Archive className="inline w-3 h-3 mr-1" /> Archived {showArchived ? 'On' : 'Off'}
          </button>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-gray-400" data-testid="stat-summary">
            {totalCount} · <span className="text-emerald-300 font-medium">{wonCount} won</span>
            {' · '}<span className="text-rose-300 font-medium">{lostCount} lost</span>
          </span>
          <button onClick={exportCsv} title="Export CSV" className="w-7 h-7 rounded-md border border-white/10 hover:bg-white/5 flex items-center justify-center text-gray-400" data-testid="export-csv-btn">
            <Download className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setShowStageManager(true)}
            title="Customize pipeline stages"
            className="w-7 h-7 rounded-md border border-white/10 hover:bg-white/5 flex items-center justify-center text-gray-400"
            data-testid="open-stage-manager"
          >
            <Settings2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Active view */}
      {viewMode === 'board' && (
        <CRMBoard
          stages={stages}
          grouped={grouped}
          showArchived={showArchived}
          onEdit={openEditModal}
          onDelete={handleDeleteContact}
          onDrop={handleStageChange}
          filterQuery={searchQuery}
        />
      )}
      {viewMode === 'list' && (
        <CRMList
          stages={stages}
          grouped={grouped}
          showArchived={showArchived}
          onEdit={openEditModal}
          onDelete={handleDeleteContact}
          onStageChange={handleStageChange}
          filterQuery={searchQuery}
        />
      )}
      {viewMode === 'calendar' && (
        <CRMCalendar
          stages={stages}
          grouped={grouped}
          showArchived={showArchived}
          onEdit={openEditModal}
          filterQuery={searchQuery}
        />
      )}

      <PipelineStageManager
        open={showStageManager}
        initialStages={stages}
        onClose={() => setShowStageManager(false)}
        onSaved={(newStages) => { setStages(newStages); fetchContacts(); }}
        getAuthHeader={getAuthHeader}
      />

      {/* Add / Edit Modal */}
      {(showAddModal || editingContact) && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-brand-surface border border-white/10 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/5 flex items-center justify-between">
              <h2 className="font-playfair text-xl text-white">
                {editingContact ? 'Edit Contact' : 'Add Contact'}
              </h2>
              <button onClick={() => { setShowAddModal(false); setEditingContact(null); resetForm(); }} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={editingContact ? handleUpdateContact : handleAddContact} className="p-6 space-y-4">
              <div>
                <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wider">Name *</label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="bg-white/5 border-white/10 text-white" data-testid="contact-name" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wider">Email</label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="contact-email" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wider">Phone</label>
                <Input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="contact-phone" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wider">Company</label>
                <Input value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="contact-company" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wider">Stage</label>
                <select value={formData.stage} onChange={(e) => setFormData({ ...formData, stage: e.target.value })} className="w-full h-10 px-3 bg-white/5 border border-white/10 rounded-md text-white" data-testid="contact-stage">
                  {stages.map((s) => (
                    <option key={s.id} value={s.id} className="bg-brand-surface">{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wider">Notes</label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className="bg-white/5 border-white/10 text-white" data-testid="contact-notes" />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setShowAddModal(false); setEditingContact(null); resetForm(); }} className="flex-1 border-white/10 text-white hover:bg-white/5">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-brand-gold text-black hover:brightness-110 font-semibold" data-testid="save-contact-btn">
                  {editingContact ? 'Update' : 'Add Contact'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppShell>
  );
}
